/**
 * Icon component using Lucide React
 */

import * as React from 'react';
import {
  Bot,
  MessageSquare,
  Zap,
  Clock,
  Plus,
  Search,
  Settings,
  Users,
  FileText,
  File,
  Database,
  Shield,
  Activity,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  X,
  Check,
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Trash2,
  Edit,
  Copy,
  MoreVertical,
  MoreHorizontal,
  Upload,
  Download,
  ExternalLink,
  RefreshCw,
  Play,
  Pause,
  Square,
  Circle,
  Moon,
  Sun,
  Menu,
  Home,
  Server,
  Loader2,
  ArrowLeft,
  Eye,
  FolderOpen,
  Tag,
  Sparkles,
  HardDrive,
  Link,
  ScanLine,
  GitBranch,
  Cloud,
} from 'lucide-react';

import { cn } from '@/lib/utils';

const icons = {
  bot: Bot,
  message: MessageSquare,
  zap: Zap,
  clock: Clock,
  plus: Plus,
  search: Search,
  settings: Settings,
  users: Users,
  'file-text': FileText,
  file: File,
  database: Database,
  shield: Shield,
  activity: Activity,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  x: X,
  check: Check,
  'check-circle': CheckCircle,
  'alert-circle': AlertCircle,
  info: Info,
  'alert-triangle': AlertTriangle,
  trash: Trash2,
  'trash-2': Trash2,
  edit: Edit,
  copy: Copy,
  'more-vertical': MoreVertical,
  'more-horizontal': MoreHorizontal,
  more: MoreHorizontal,
  upload: Upload,
  download: Download,
  'external-link': ExternalLink,
  refresh: RefreshCw,
  'refresh-cw': RefreshCw,
  play: Play,
  pause: Pause,
  stop: Square,
  circle: Circle,
  moon: Moon,
  sun: Sun,
  menu: Menu,
  home: Home,
  server: Server,
  loader: Loader2,
  'arrow-left': ArrowLeft,
  eye: Eye,
  'folder-open': FolderOpen,
  tag: Tag,
  sparkles: Sparkles,
  'hard-drive': HardDrive,
  link: Link,
  scan: ScanLine,
  'git-branch': GitBranch,
  cloud: Cloud,
} as const;

export type IconName = keyof typeof icons;

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number | string;
  className?: string;
}

export function Icon({ name, size = 20, className, ...props }: IconProps) {
  const LucideIcon = icons[name];
  
  if (!LucideIcon) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  return (
    <LucideIcon
      size={size}
      className={cn('shrink-0', className)}
      {...props}
    />
  );
}

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost' | 'outline';
  label?: string;
}

export function IconButton({
  icon,
  size = 'md',
  variant = 'ghost',
  label,
  className,
  ...props
}: IconButtonProps) {
  const sizeClasses = {
    sm: 'h-7 w-7',
    md: 'h-9 w-9',
    lg: 'h-11 w-11',
  };

  const iconSizes = {
    sm: 14,
    md: 18,
    lg: 22,
  };

  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
  };

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...props}
    >
      <Icon name={icon} size={iconSizes[size]} />
    </button>
  );
}
